// react
import React from 'react';
import { Link } from 'react-router-dom';

// Utils
import cn from '@/utils/cn';

// Components
import SocialLinks from '@/components/molecules/SocialLinks/SocialLinks';
import BreadCrumb from '@/components/atoms/BreadCrumb/BreadCrumb';
import StrapiMedia from '@/components/atoms/StrapiMedia/StrapiMedia';
import { ColumnName } from '@/enum/ColumnName';
import FooterContactSection from './FooterContactSection/FooterContactSection';

// Types
import { FooterType, FooterMenuType } from './FooterType';

// Styles
import styles from './Footer.module.css';

interface FooterProps {
  groups: FooterType['groups'];
  description: FooterType['description'];
  phone: FooterType['phone'];
  mail: FooterType['mail'];
  addresses: FooterType['addresses'];
  socialMedia: FooterType['socialMedia'];
  preFooter: FooterType['preFooter'];
  partners: FooterType['partners'];
  legalLinks: FooterType['legalLinks'];
  copyright: FooterType['copyright'];
  logo: FooterType['logo'];
  breadcrumb?: boolean;
}

function Footer({
  groups = null,
  description,
  phone = null,
  mail = null,
  addresses = null,
  socialMedia = null,
  preFooter,
  partners,
  legalLinks = null,
  copyright,
  logo,
  breadcrumb = false,
}: FooterProps) {
  const getSection = (section: FooterMenuType, index: number): React.ReactNode => (
    <div key={index} className={styles.section}>
      {section.label ? <p className={cn([styles.sectionTitle, 'font-uppercase'])}>{section.label}</p> : null}
      {section.links
        ? section.links.map((link) => (
          <Link key={link.id} to={link.link} className={styles.link}>
            {link.label}
          </Link>
        ))
        : null}
    </div>
  );

  return (
    <div className={styles.footer}>
      <FooterContactSection preFooter={preFooter} />

      {breadcrumb && groups && groups.length > 0 ? <BreadCrumb items={groups} /> : null}

      <div className={cn([styles.footerContainer, 'max-width-wrapper'])}>
        <div className={styles.wrapper}>
          <div className={styles.links}>
            <div className={styles.text}>{description}</div>
            <div className={styles.columns}>
              {groups && groups?.length > 0 && (
                <>
                  <div className={styles.columnOne}>
                    {groups
                      .filter((section) => section.columns === ColumnName.ONE)
                      .map((section, index) => getSection(section, index))}
                  </div>
                  <div className={styles.columnTwo}>
                    {groups
                      .filter((section) => section.columns === ColumnName.TWO)
                      .map((section, index) => getSection(section, index))}
                  </div>
                  <div className={styles.columnThree}>
                    {groups
                      .filter((section) => section.columns === ColumnName.THREE)
                      .map((section, index) => getSection(section, index))}
                  </div>
                </>
              )}

              <div className={styles.infos}>
                <div className={styles.contactInfos}>
                  {phone ? (
                    <a href={`tel:${phone}`} className={styles.infosTitle}>
                      {phone}
                    </a>
                  ) : null}
                  {mail ? (
                    <a href={`mailto:${mail}`} className={styles.infosTitle}>
                      {mail}
                    </a>
                  ) : null}
                </div>
                {addresses && addresses.length > 0
                  ? addresses.map((address) => (
                    <div key={`${address.id}_${address?.city}`}>
                      {address.city ? <p className={styles.infosTitle}>{address.city}</p> : null}
                      {address.street ? <p>{address.street}</p> : null}
                      {address.detail ? <p>{address.detail}</p> : null}
                    </div>
                  ))
                  : null}
                <div className={styles.socials}>
                  <SocialLinks links={socialMedia} />
                </div>
                <StrapiMedia image={logo} format="small" className={styles.zolLogo} />
              </div>
            </div>
          </div>
        </div>

        <SocialLinks links={socialMedia} className={styles.socialsMobile} />

        <div className={styles.logosWrapper}>
          <div className={styles.logos}>
            {partners.map((partner) => (
              <Link
                key={`partner-${partner.id}`}
                to={partner.link}
                className={styles.logoLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StrapiMedia image={partner.logo} format="thumbnail" className={styles.logoImage} />
              </Link>
            ))}
          </div>
          <StrapiMedia image={logo} format="small" className={styles.zolLogo} />
        </div>

        <div className={styles.legals}>
          <div className={styles.legalsLinks}>
            {legalLinks && legalLinks?.length > 0
              ? legalLinks.map((link) => (
                <Link key={`legal-link-${link.id}`} to={link.link} className={styles.legalsLink}>
                  {link.label}
                </Link>
              ))
              : null}
          </div>

          <div className={styles.copyright}>{copyright}</div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
